import React, { useState, useEffect } from 'react';
import { Shield, Save, RefreshCw, CheckCircle, XCircle, Zap, Globe, Settings, GripVertical } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import './ModulePermissionsPage.css';

const ModulePermissionsPage = () => {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Settings state
  const [settings, setSettings] = useState({
    siteUrl: '',
    isActive: false,
    turkish: false,
    english: false,
    arabic: false,
    french: false,
    german: false,
    isServiceHasImage: false,
    isBlogCategoryOn: false,
  });

  // Modül tanımları - API'deki ModuleType enum ile eşleşmeli
  const moduleDefinitions = [
    { id: 1, key: 'dashboard', name: 'Dashboard', description: 'Ana sayfa ve istatistikler' },
    { id: 2, key: 'settings', name: 'Site Ayarları', description: 'Genel site ayarları' },
    { id: 3, key: 'change-password', name: 'Şifre Değiştir', description: 'Kullanıcı şifre değiştirme' },
    { id: 4, key: 'services', name: 'Hizmetler', description: 'Hizmet yönetimi' },
    { id: 5, key: 'service-categories', name: 'Hizmet Kategorileri', description: 'Hizmet kategori yönetimi' },
    { id: 6, key: 'logo', name: 'Logo', description: 'Site logosu yönetimi' },
    { id: 7, key: 'banner', name: 'Banner', description: 'Banner yönetimi' },
    { id: 8, key: 'blog', name: 'Blog', description: 'Blog yazıları yönetimi' },
    { id: 9, key: 'referans', name: 'Referanslar', description: 'Referans firmaları yönetimi' },
    { id: 10, key: 'contracted-banks', name: 'Anlaşmalı Bankalar', description: 'Anlaşmalı banka yönetimi' },
    { id: 11, key: 'contracted-institutions', name: 'Anlaşmalı Kurumlar', description: 'Anlaşmalı kurum yönetimi' },
    { id: 12, key: 'faq', name: 'SSS', description: 'Sık sorulan sorular yönetimi' },
    { id: 13, key: 'galeri', name: 'Galeri', description: 'Galeri ve resim yönetimi' },
    { id: 14, key: 'testimonial', name: 'Yorumlar', description: 'Müşteri yorumları yönetimi' },
    { id: 15, key: 'employee', name: 'Ekip', description: 'Çalışan yönetimi' },
    { id: 16, key: 'products', name: 'Ürünler', description: 'Ürün yönetimi' },
    { id: 17, key: 'product-categories', name: 'Ürün Kategorileri', description: 'Ürün kategori yönetimi' }
  ];

  useEffect(() => {
    fetchPermissions();
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/setting');
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Ayarlar yüklenirken hata:', error);
      // Hata durumunda varsayılan değerleri koru
    }
  };

  const fetchPermissions = async () => {
    setLoading(true);
    try {
      const response = await api.get('/customermodulepermission');
      const existingPermissions = response.data;

      // Tüm modülleri liste olarak hazırla
      const allPermissions = moduleDefinitions.map(module => {
        const existing = existingPermissions.find(p => p.moduleType === module.id);
        return {
          moduleType: module.id,
          moduleName: module.name,
          moduleKey: module.key,
          description: module.description,
          isEnabled: existing ? existing.isEnabled : false,
          orderNumber: existing ? existing.orderNumber : 0,
          customerModulePermissionId: existing?.customerModulePermissionId || null
        };
      });

      // OrderNumber'a göre sırala
      allPermissions.sort((a, b) => a.orderNumber - b.orderNumber);

      setPermissions(allPermissions);
    } catch (error) {
      console.error('Modül yetkileri yüklenirken hata:', error);
      
      // CORS veya bağlantı hatası durumunda varsayılan modülleri göster
      const defaultPermissions = moduleDefinitions.map(module => ({
        moduleType: module.id,
        moduleName: module.name,
        moduleKey: module.key,
        description: module.description,
        isEnabled: true, // Varsayılan olarak tüm modüller aktif
        orderNumber: 0,
        customerModulePermissionId: null
      }));
      
      setPermissions(defaultPermissions);
      
      if (error.code === 'ERR_NETWORK') {
        toast.error('API bağlantısı kurulamadı. Tüm modüller varsayılan olarak gösteriliyor.');
      } else {
        toast.error('Modül yetkileri yüklenemedi');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (moduleType) => {
    setPermissions(prev =>
      prev.map(p =>
        p.moduleType === moduleType
          ? { ...p, isEnabled: !p.isEnabled }
          : p
      )
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Modül yetkilerini kaydet
      const permissionsToUpdate = permissions.map(p => ({
        moduleType: p.moduleType,
        isEnabled: p.isEnabled,
        orderNumber: p.orderNumber
      }));

      await api.post('/customermodulepermission/bulk-update', permissionsToUpdate);
      
      // Settings'i kaydet
      try {
        await api.put('/setting', settings);
      } catch (err) {
        if (err.response?.status === 404) {
          await api.post('/setting', settings);
        }
      }
      
      toast.success('Tüm ayarlar başarıyla güncellendi');
      
      // Cache'i yenilemek için tekrar yükle
      await fetchPermissions();
      await fetchSettings();
    } catch (error) {
      console.error('Ayarlar güncellenirken hata:', error);
      toast.error('Ayarlar güncellenemedi');
    } finally {
      setSaving(false);
    }
  };

  const handleSettingsChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleEnableAll = () => {
    setPermissions(prev => prev.map(p => ({ ...p, isEnabled: true })));
  };

  const handleDisableAll = () => {
    setPermissions(prev => prev.map(p => ({ ...p, isEnabled: false })));
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(permissions);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // OrderNumber'ları yeniden ata (0'dan başlayarak)
    const updatedItems = items.map((item, index) => ({
      ...item,
      orderNumber: index
    }));

    setPermissions(updatedItems);
    toast.success('Sıralama değiştirildi. Kaydetmeyi unutmayın!');
  };

  const enabledCount = permissions.filter(p => p.isEnabled).length;

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-header-content">
          <div className="page-icon-wrapper">
            <Shield className="page-icon" />
          </div>
          <div>
            <h1 className="page-title">Sistem Ayarları</h1>
            <p className="page-description">
              Modül görünürlüğü, dil ayarları ve genel sistem yapılandırması
            </p>
          </div>
        </div>
        <div className="page-actions">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || loading}
            className="btn btn-primary"
          >
            <Save size={18} />
            {saving ? 'Kaydediliyor...' : 'Tüm Değişiklikleri Kaydet'}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="module-stats">
        <div className="stat-card stat-total">
          <div className="stat-icon">
            <Shield size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{permissions.length}</div>
            <div className="stat-label">Toplam Modül</div>
          </div>
        </div>
        <div className="stat-card stat-active">
          <div className="stat-icon">
            <CheckCircle size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{enabledCount}</div>
            <div className="stat-label">Aktif Modül</div>
          </div>
        </div>
        <div className="stat-card stat-inactive">
          <div className="stat-icon">
            <XCircle size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{permissions.length - enabledCount}</div>
            <div className="stat-label">Pasif Modül</div>
          </div>
        </div>
        <div className="stat-card stat-percentage">
          <div className="stat-icon">
            <Zap size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">
              {permissions.length > 0 ? Math.round((enabledCount / permissions.length) * 100) : 0}%
            </div>
            <div className="stat-label">Aktiflik Oranı</div>
          </div>
        </div>
      </div>

      {/* Genel Ayarlar */}
      <div className="content-card">
        <div className="card-section-header">
          <Globe size={20} />
          <h3>Genel Ayarlar</h3>
        </div>
        <div className="form-row">
          <div className="form-field">
            <label>Site URL</label>
            <input
              type="text"
              name="siteUrl"
              value={settings.siteUrl}
              onChange={handleSettingsChange}
              className="form-input"
              placeholder="https://example.com"
            />
          </div>
          <div className="form-field">
            <label className="toggle-label">
              <span>Site Aktif</span>
              <label className="toggle">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={settings.isActive}
                  onChange={handleSettingsChange}
                />
                <span className="toggle-slider" />
              </label>
            </label>
          </div>
        </div>
      </div>

      {/* Dil Ayarları */}
      <div className="content-card">
        <div className="card-section-header">
          <Globe size={20} />
          <h3>Dil Ayarları</h3>
        </div>
        <div className="toggle-group">
          {[
            { name: 'turkish', label: 'Türkçe', flag: '🇹🇷' },
            { name: 'english', label: 'İngilizce', flag: '🇬🇧' },
            { name: 'arabic', label: 'Arapça', flag: '🇸🇦' },
            { name: 'french', label: 'Fransızca', flag: '🇫🇷' },
            { name: 'german', label: 'Almanca', flag: '🇩🇪' },
          ].map((lang) => (
            <div className="form-field" key={lang.name}>
              <label className="toggle-label">
                <span>
                  {lang.flag} {lang.label}
                </span>
                <label className="toggle">
                  <input
                    type="checkbox"
                    name={lang.name}
                    checked={settings[lang.name]}
                    onChange={handleSettingsChange}
                  />
                  <span className="toggle-slider" />
                </label>
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Diğer Ayarlar */}
      <div className="content-card">
        <div className="card-section-header">
          <Settings size={20} />
          <h3>Diğer Ayarlar</h3>
        </div>
        <div className="toggle-group">
          <div className="form-field">
            <label className="toggle-label">
              <span>Hizmetlerde Görsel</span>
              <label className="toggle">
                <input
                  type="checkbox"
                  name="isServiceHasImage"
                  checked={settings.isServiceHasImage}
                  onChange={handleSettingsChange}
                />
                <span className="toggle-slider" />
              </label>
            </label>
          </div>
          <div className="form-field">
            <label className="toggle-label">
              <span>Blog Kategorileri</span>
              <label className="toggle">
                <input
                  type="checkbox"
                  name="isBlogCategoryOn"
                  checked={settings.isBlogCategoryOn}
                  onChange={handleSettingsChange}
                />
                <span className="toggle-slider" />
              </label>
            </label>
          </div>
        </div>
      </div>

      {/* Modül Yetkileri - Tablo Format */}
      {/* Modül Yetkileri - Tablo Format */}
      <div className="content-card">
        <div className="card-section-header">
          <Shield size={20} />
          <h3>Modül Yetkileri</h3>
        </div>
        <div style={{ marginBottom: '16px', display: 'flex', gap: '8px', paddingLeft: '20px' }}>
          <button
            type="button"
            onClick={handleEnableAll}
            className="btn-sm btn-secondary"
          >
            <CheckCircle size={16} />
            Tümünü Aktif Et
          </button>
          <button
            type="button"
            onClick={handleDisableAll}
            className="btn-sm btn-secondary"
          >
            <XCircle size={16} />
            Tümünü Pasif Et
          </button>
        </div>

        {loading ? (
          <div style={{ padding: '60px 20px', textAlign: 'center' }}>
            <RefreshCw size={32} className="spinning" style={{ marginBottom: '12px' }} />
            <p className="text-muted">Modüller yükleniyor...</p>
          </div>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}></th>
                    <th style={{ width: '60px' }}>ID</th>
                    <th style={{ width: '80px', textAlign: 'center' }}>Sıra</th>
                    <th>Modül Adı</th>
                    <th>Açıklama</th>
                    <th style={{ width: '140px', textAlign: 'center' }}>Durum</th>
                    <th style={{ width: '120px', textAlign: 'center' }}>Aktif/Pasif</th>
                  </tr>
                </thead>
                <Droppable droppableId="modules">
                  {(provided, snapshot) => (
                    <tbody
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      style={{
                        backgroundColor: snapshot.isDraggingOver ? 'rgba(99, 102, 241, 0.05)' : 'transparent'
                      }}
                    >
                      {permissions.map((permission, index) => (
                        <Draggable
                          key={permission.moduleType.toString()}
                          draggableId={permission.moduleType.toString()}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <tr
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              style={{
                                ...provided.draggableProps.style,
                                backgroundColor: snapshot.isDragging ? 'rgba(99, 102, 241, 0.1)' : 'white',
                                boxShadow: snapshot.isDragging ? '0 4px 12px rgba(0,0,0,0.15)' : 'none'
                              }}
                            >
                              <td
                                {...provided.dragHandleProps}
                                style={{
                                  cursor: 'grab',
                                  textAlign: 'center',
                                  padding: '12px 8px'
                                }}
                              >
                                <GripVertical
                                  size={18}
                                  style={{
                                    color: snapshot.isDragging ? 'var(--primary)' : 'var(--text-muted)',
                                    transition: 'color 0.2s'
                                  }}
                                />
                              </td>
                              <td>
                                <span style={{ 
                                  fontWeight: '700', 
                                  color: 'var(--text-muted)',
                                  fontFamily: 'monospace'
                                }}>
                                  #{permission.moduleType}
                                </span>
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <span style={{
                                  display: 'inline-block',
                                  width: '40px',
                                  height: '40px',
                                  lineHeight: '40px',
                                  background: 'linear-gradient(135deg, var(--primary), #818cf8)',
                                  color: 'white',
                                  borderRadius: '8px',
                                  fontSize: '14px',
                                  fontWeight: '700'
                                }}>
                                  {permission.orderNumber}
                                </span>
                              </td>
                              <td>
                                <div>
                                  <strong>{permission.moduleName}</strong>
                                  <br />
                                  <code style={{
                                    fontSize: '12px',
                                    background: 'var(--bg-secondary)',
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    color: 'var(--primary)'
                                  }}>
                                    {permission.moduleKey}
                                  </code>
                                </div>
                              </td>
                              <td className="text-muted">{permission.description}</td>
                              <td style={{ textAlign: 'center' }}>
                                <span className={`badge ${permission.isEnabled ? 'badge-success' : 'badge-gray'}`}>
                                  {permission.isEnabled ? (
                                    <>
                                      <CheckCircle size={14} style={{ marginRight: '4px' }} />
                                      Aktif
                                    </>
                                  ) : (
                                    <>
                                      <XCircle size={14} style={{ marginRight: '4px' }} />
                                      Pasif
                                    </>
                                  )}
                                </span>
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <label className="toggle">
                                  <input
                                    type="checkbox"
                                    checked={permission.isEnabled}
                                    onChange={() => handleToggle(permission.moduleType)}
                                  />
                                  <span className="toggle-slider"></span>
                                </label>
                              </td>
                            </tr>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </tbody>
                  )}
                </Droppable>
              </table>
            </div>
          </DragDropContext>
        )}
      </div>

      <div className="form-actions" style={{ 
        position: 'sticky', 
        bottom: '20px', 
        background: 'white', 
        padding: '20px', 
        borderRadius: '12px',
        boxShadow: '0 -4px 20px rgba(0,0,0,0.1)',
        zIndex: 10,
        marginTop: '24px'
      }}>
        <button 
          type="button" 
          onClick={handleSave} 
          className="btn btn-primary btn-full" 
          disabled={saving || loading}
        >
          {saving ? (
            <>
              <RefreshCw size={18} className="spinning" /> Kaydediliyor...
            </>
          ) : (
            <>
              <Save size={18} /> Tüm Ayarları Kaydet
            </>
          )}
        </button>
      </div>

      <div className="info-banner">
        <div className="info-banner-icon">
          <Shield size={20} />
        </div>
        <div className="info-banner-content">
          <h3 className="info-banner-title">Önemli Bilgi</h3>
          <p className="info-banner-text">
            Bu sayfada yaptığınız tüm değişiklikler kaydedildiğinde sisteme uygulanacaktır. 
            Pasif hale getirilen modüller sidebar menüsünde görünmeyecek, aktif diller form alanlarında gösterilecektir.
            "Sistem Ayarları" menüsü her zaman görünür olacaktır.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ModulePermissionsPage;
